<template>
  <ion-page>
    <ion-header class="ion-no-border events-page-header" :class="{ 'page-header--scrolled': headerScrolled }">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-back-button text="" default-href="/admin-events"></ion-back-button>
        </ion-buttons>
        <ion-title>{{ event?.title || 'Регистрации' }}</ion-title>
        <ion-buttons slot="end" class="registrations-header-actions">
          <HeaderMessengerButton />
          <ion-button @click="onOpenScannerClick">
            <ion-icon :icon="qrCodeOutline"></ion-icon>
            <span class="header-btn-label">Сканер QR</span>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="main-background events-content" :fullscreen="true" @ionScroll="onContentScroll">
      <ion-refresher slot="fixed" @ionRefresh="onRefresh">
        <ion-refresher-content></ion-refresher-content>
      </ion-refresher>
      <div class="ion-padding">
        <p v-if="loading" class="ion-text-center">Загрузка...</p>
        <ion-list v-else lines="full" class="registrations-list">
          <ion-item v-for="r in registrations" :key="r.id" class="registration-item">
            <ion-label>
              <h2>{{ r.userFirstName }} {{ r.userLastName }}</h2>
              <p>{{ r.userLogin }} · {{ r.userEmail }}</p>
              <p>{{ formatDate(r.registeredAt) }}</p>
            </ion-label>
            <div class="registration-chips" slot="end">
              <ion-badge v-if="event?.isPaid" :color="r.paid ? 'success' : 'warning'">
                {{ r.paid ? 'Оплачено' : 'Не оплачено' }}
              </ion-badge>
              <ion-badge :color="r.checkedInAt ? 'success' : 'medium'">
                {{ r.checkedInAt ? `Вход ${formatCheckInTime(r.checkedInAt)}` : 'Не отмечен' }}
              </ion-badge>
            </div>
          </ion-item>
          <p v-if="registrations.length === 0" class="ion-text-center registrations-empty">Нет регистраций.</p>
        </ion-list>
      </div>

      <!-- Сканер QR: только камера с визуализацией фокуса -->
      <ion-modal
        :is-open="showScanner"
        @didDismiss="onScannerClose"
        @ionDidPresent="onScannerPresent"
        class="qr-scanner-modal"
        :initial-breakpoint="1"
        :breakpoints="[0, 1]"
      >
        <ion-header class="scanner-header">
          <ion-toolbar>
            <ion-title>Сканирование QR-кода</ion-title>
            <ion-buttons slot="end">
              <ion-button class="scanner-close-btn" @click="onScannerClose">Закрыть</ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content class="scanner-content" fullscreen>
          <div class="scanner-wrapper">
            <video ref="scannerVideo" class="scanner-video" playsinline muted autoplay></video>
            <canvas ref="scannerCanvas" class="scanner-canvas"></canvas>
            <div class="scanner-overlay">
              <div class="scanner-frame"></div>
              <p class="scanner-hint">Наведите камеру на QR-код участника</p>
            </div>
          </div>
          <!-- Результат проверки -->
          <div v-if="scanResult !== null" class="scan-result-overlay" :class="scanResult.success ? 'success' : 'fail'">
            <div class="scan-result-icon">
              <ion-icon v-if="scanResult.success" :icon="checkmarkCircleOutline"></ion-icon>
              <ion-icon v-else :icon="closeCircleOutline"></ion-icon>
            </div>
            <p class="scan-result-text">{{ scanResult.message }}</p>
            <p v-if="scanResult.user" class="scan-result-user">{{ scanResult.user }}</p>
            <ion-button class="scan-result-rescan-btn" size="small" fill="outline" @click="scanResult = null; startCamera()">Сканировать ещё</ion-button>
          </div>
          <p v-if="cameraError" class="scanner-error">{{ cameraError }}</p>
        </ion-content>
      </ion-modal>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import {
  IonBackButton,
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  IonToolbar,
  isPlatform
} from '@ionic/vue';
import { Capacitor } from '@capacitor/core';
import { Camera } from '@capacitor/camera';
import { checkmarkCircleOutline, closeCircleOutline, qrCodeOutline } from 'ionicons/icons';
import { defineComponent } from 'vue';
import { useRoute } from 'vue-router';
import { ILigaEvent } from '@/model/liga-event.model';
import { ILigaEventRegistration } from '@/model/liga-event-registration.model';
import eventsService from '@/services/events.service';
import dayjs from 'dayjs';
import jsQR from 'jsqr';

import HeaderMessengerButton from '@/components/HeaderMessengerButton.vue';

export default defineComponent({
  name: 'AdminEventRegistrations',
  components: {
    HeaderMessengerButton,
    IonBackButton,
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonPage,
    IonRefresher,
    IonRefresherContent,
    IonTitle,
    IonToolbar
  },
  setup() {
    const route = useRoute();
    return { route };
  },
  data() {
    return {
      event: null as ILigaEvent | null,
      registrations: [] as ILigaEventRegistration[],
      loading: true,
      headerScrolled: false,
      showScanner: false,
      verifying: false,
      scanResult: null as { success: boolean; message: string; user?: string } | null,
      cameraError: '',
      stream: null as MediaStream | null,
      scanAnimationId: null as number | null,
      scanIntervalId: null as ReturnType<typeof setInterval> | null,
      scannerOpenTimer: 0 as ReturnType<typeof setTimeout> | 0,
      eventsService,
      checkmarkCircleOutline,
      closeCircleOutline,
      qrCodeOutline
    };
  },
  watch: {
    showScanner(isOpen: boolean) {
      if (isOpen) {
        this.scanResult = null;
        this.cameraError = '';
        // Запуск камеры по факту открытия модалки (ionDidPresent может не срабатывать в Ionic Vue)
        console.log('[QR Scanner] showScanner=true, scheduling startCamera');
        const t = setTimeout(() => {
          if (this.showScanner) this.startCamera();
        }, 400);
        this.scannerOpenTimer = t;
      } else {
        if (this.scannerOpenTimer) {
          clearTimeout(this.scannerOpenTimer);
          this.scannerOpenTimer = 0;
        }
        this.stopCamera();
      }
    }
  },
  async mounted() {
    const id = Number(this.route.params.id);
    if (!id) {
      this.loading = false;
      return;
    }
    try {
      this.event = await eventsService.getEvent(id);
      this.registrations = await eventsService.getRegistrations(id);
    } catch (e) {
      this.registrations = [];
    }
    this.loading = false;
  },
  methods: {
    async onRefresh(event: CustomEvent) {
      const id = Number(this.route.params.id);
      if (id) {
        try {
          this.event = await eventsService.getEvent(id);
          this.registrations = await eventsService.getRegistrations(id);
        } catch (e) {
          this.registrations = [];
        }
      }
      (event.target as HTMLIonRefresherElement)?.complete();
    },
    onContentScroll(e: CustomEvent) {
      const scrollTop = e.detail?.scrollTop ?? 0;
      this.headerScrolled = scrollTop > 16;
    },
    formatDate(iso: string) {
      return dayjs(iso).format('D MMM YYYY, HH:mm');
    },
    formatCheckInTime(iso: string) {
      return dayjs(iso).format('HH:mm');
    },
    onOpenScannerClick() {
      console.log('[QR Scanner] Button clicked, opening modal');
      this.showScanner = true;
    },
    onScannerClose() {
      this.showScanner = false;
      this.stopCamera();
    },
    onScannerPresent() {
      console.log('[QR Scanner] ionDidPresent fired');
    },
    async startCamera(retryCount = 0) {
      this.stopCamera();
      // refs могут быть пустыми, если контент модалки рендерится в портале (Ionic)
      let video = this.$refs.scannerVideo as HTMLVideoElement | undefined;
      let canvas = this.$refs.scannerCanvas as HTMLCanvasElement | undefined;
      if (!video || !canvas) {
        const modalEl = document.querySelector('.qr-scanner-modal');
        if (modalEl) {
          const root = modalEl.shadowRoot ?? modalEl;
          video = video ?? (root.querySelector('video.scanner-video') as HTMLVideoElement) ?? undefined;
          canvas = canvas ?? (root.querySelector('canvas.scanner-canvas') as HTMLCanvasElement) ?? undefined;
        }
      }
      console.log('[QR Scanner] startCamera', { retryCount, hasVideo: !!video, hasCanvas: !!canvas, showScanner: this.showScanner });
      if (!video || !canvas) {
        if (retryCount < 8 && this.showScanner) {
          setTimeout(() => this.startCamera(retryCount + 1), 200);
        } else if (this.showScanner) {
          this.cameraError = 'Не удалось подключить камеру. Закройте и откройте сканер снова.';
          console.warn('[QR Scanner] Video/canvas refs not available after retries');
        }
        return;
      }
      // MDN: getUserMedia доступен только в secure context (HTTPS, localhost, file://).
      // В небезопасном контексте navigator.mediaDevices === undefined.
      const isSecure = typeof window !== 'undefined' && window.isSecureContext;
      const hasMediaDevices = typeof navigator !== 'undefined' && navigator.mediaDevices != null;
      if (!isSecure || !hasMediaDevices) {
        console.warn('[QR Scanner] Insecure context or no mediaDevices', { isSecure, hasMediaDevices });
        this.cameraError = 'Камера доступна только по HTTPS или с localhost. Откройте приложение по безопасному адресу.';
        return;
      }
      if (typeof navigator.mediaDevices.getUserMedia !== 'function') {
        this.cameraError = 'Камера не поддерживается в этом браузере.';
        console.warn('[QR Scanner] getUserMedia is not a function');
        return;
      }
      // Запрос разрешений через Capacitor только в нативном приложении; в мобильном браузере — только getUserMedia
      const isNative = Capacitor.isNativePlatform();
      if (isNative && (isPlatform('android') || isPlatform('ios'))) {
        try {
          const status = await Camera.requestPermissions({ permissions: ['camera'] });
          if (status.camera !== 'granted' && status.camera !== 'prompt') {
            this.cameraError = 'Нет доступа к камере. Разрешите в настройках приложения.';
            return;
          }
        } catch (e) {
          console.warn('Camera.requestPermissions error', e);
        }
      }
      // Фронтальная камера только на десктопе (MacBook и т.д.); на мобильном (браузер и приложение) — задняя
      const isMobile = isPlatform('android') || isPlatform('ios');
      const useFrontCamera = !isMobile;
      const videoConstraints: MediaTrackConstraints = useFrontCamera
        ? { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }
        : { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } };

      const tryGetUserMedia = async (constraints: MediaTrackConstraints | boolean) => {
        console.log('[QR Scanner] getUserMedia request', { video: constraints });
        const stream = await navigator.mediaDevices.getUserMedia({ video: constraints });
        this.stream = stream;
        video.srcObject = stream;
        await video.play();
        this.cameraError = '';
        this.tick();
        if (isPlatform('android')) {
          if (this.scanIntervalId) clearInterval(this.scanIntervalId);
          this.scanIntervalId = setInterval(() => {
            if (this.showScanner && !this.scanResult && !this.verifying) this.tick();
          }, 180);
        }
        console.log('[QR Scanner] Camera started successfully');
      };

      try {
        await tryGetUserMedia(videoConstraints);
      } catch (e: unknown) {
        const err = e as { name?: string; message?: string };
        console.warn('[QR Scanner] getUserMedia error:', err?.name, err?.message, err);
        // На мобильном браузере при отказе в environment пробуем user (фронтальная)
        if (!useFrontCamera && (err.name === 'NotFoundError' || err.name === 'OverconstrainedError')) {
          try {
            await tryGetUserMedia({ facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } });
            return;
          } catch (e2) {
            console.warn('getUserMedia fallback (user) error:', e2);
          }
        }
        // Минимальные ограничения — часто срабатывает в мобильном браузере, когда идеальные не поддерживаются
        if (err.name === 'OverconstrainedError' || err.name === 'NotFoundError') {
          try {
            await tryGetUserMedia(useFrontCamera ? { facingMode: 'user' } : { facingMode: 'environment' });
            return;
          } catch (e2) {
            console.warn('getUserMedia fallback (facingMode only) error:', e2);
          }
        }
        try {
          await tryGetUserMedia(true);
          return;
        } catch (e3) {
          console.warn('getUserMedia fallback (video: true) error:', e3);
        }
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          this.cameraError = 'Нет доступа к камере. Разрешите использование камеры в настройках браузера (иконка замка в адресной строке).';
        } else if (err.name === 'NotFoundError') {
          this.cameraError = 'Камера не найдена.';
        } else if (err.name === 'NotReadableError') {
          this.cameraError = 'Камера занята другим приложением.';
        } else {
          this.cameraError = 'Не удалось запустить камеру. Проверьте, что сайт открыт по HTTPS или localhost.';
        }
        console.warn('[QR Scanner] Final cameraError:', this.cameraError, 'Error:', err?.name, err?.message);
      }
    },
    stopCamera() {
      if (this.scanAnimationId != null) {
        cancelAnimationFrame(this.scanAnimationId);
        this.scanAnimationId = null;
      }
      if (this.scanIntervalId != null) {
        clearInterval(this.scanIntervalId);
        this.scanIntervalId = null;
      }
      if (this.stream) {
        this.stream.getTracks().forEach(t => t.stop());
        this.stream = null;
      }
      const video = this.$refs.scannerVideo as HTMLVideoElement | undefined;
      if (video) video.srcObject = null;
    },
    tick() {
      const video = this.$refs.scannerVideo as HTMLVideoElement | undefined;
      const canvas = this.$refs.scannerCanvas as HTMLCanvasElement | undefined;
      if (!video || !canvas || !video.videoWidth || this.scanResult !== null || !this.showScanner) {
        if (this.showScanner && video?.videoWidth && !this.scanResult) this.scanAnimationId = requestAnimationFrame(() => this.tick());
        return;
      }
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        this.scanAnimationId = requestAnimationFrame(() => this.tick());
        return;
      }
      ctx.drawImage(video, 0, 0);
      const imageData = ctx.getImageData(0, 0, w, h);
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      if (code?.data) {
        const parts = code.data.split(':');
        if (parts.length === 2) {
          const eventId = parseInt(parts[0], 10);
          const registrationId = parseInt(parts[1], 10);
          if (!isNaN(eventId) && !isNaN(registrationId)) {
            this.doVerify(eventId, registrationId);
            return;
          }
        }
      }
      this.scanAnimationId = requestAnimationFrame(() => this.tick());
    },
    async doVerify(eventId: number, registrationId: number) {
      this.verifying = true;
      this.scanResult = null;
      try {
        // Сначала только проверка без отметки входа, чтобы не отмечать неоплаченных на платное событие
        const verifyResult = await eventsService.verifyQr(eventId, registrationId, false);
        if (verifyResult) {
          const reg = verifyResult.registration;
          const isPaidEvent = this.event?.isPaid ?? false;
          const isRegistrationPaid = reg.paid === true;
          if (isPaidEvent && !isRegistrationPaid) {
            this.scanResult = {
              success: false,
              message: 'Участие не оплачено. Оплатите для входа.',
              user: `${reg.userFirstName || ''} ${reg.userLastName || ''}`.trim() || reg.userLogin
            };
            this.verifying = false;
            return;
          }
          // Разрешён вход — отмечаем и обновляем список (или показываем, что проход уже был)
          const checkInResult = await eventsService.verifyQr(eventId, registrationId, true);
          if (checkInResult?.alreadyCheckedIn) {
            this.scanResult = {
              success: false,
              message: 'По данному коду уже выполнен проход',
              user: `${reg.userFirstName || ''} ${reg.userLastName || ''}`.trim() || reg.userLogin
            };
          } else {
            this.scanResult = {
              success: true,
              message: 'Вход подтверждён',
              user: `${reg.userFirstName || ''} ${reg.userLastName || ''}`.trim() || reg.userLogin
            };
          }
          const currentEventId = this.event?.id;
          if (currentEventId != null) {
            const list = await eventsService.getRegistrations(currentEventId);
            this.registrations = list;
          }
        } else {
          this.scanResult = { success: false, message: 'Регистрация не найдена или неверное событие' };
        }
      } catch (e) {
        this.scanResult = { success: false, message: 'Ошибка проверки' };
      }
      this.verifying = false;
    }
  }
});
</script>

<style scoped>
.registration-item {
  --background: var(--liga-surface);
  --color: var(--liga-chrome-text);
  --border-color: var(--liga-surface-border);
}
.registration-item h2,
.registration-item p {
  color: var(--liga-chrome-text);
}
.registration-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  justify-content: flex-end;
}
.registrations-empty {
  color: var(--liga-chrome-text);
}

/* QR Scanner modal: камера + оверлей фокуса */
.qr-scanner-modal ion-content.scanner-content {
  --background: #000;
  --padding-top: 0;
  --padding-bottom: 0;
}
.scanner-header ion-toolbar {
  --background: rgba(30, 30, 30, 0.95);
  --color: var(--liga-chrome-text);
}
.scanner-header ion-title {
  color: var(--liga-chrome-text) !important;
}
.scanner-close-btn {
  --color: #e85d04;
  color: #e85d04;
  font-weight: 600;
}
.scanner-close-btn:hover,
.scanner-close-btn.ion-activated {
  --color: #f07d34;
  color: #f07d34;
}
.scanner-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 60vh;
  overflow: hidden;
  background: #000;
}
.scanner-video {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  object-fit: cover;
}
.scanner-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  pointer-events: none;
  opacity: 0;
}
.scanner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.scanner-frame {
  width: 260px;
  height: 260px;
  border: 3px solid var(--ion-color-orange);
  border-radius: 20px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
  animation: scanner-pulse 2s ease-in-out infinite;
}
.scanner-hint {
  margin-top: 24px;
  padding: 0 24px;
  color: var(--liga-chrome-text);
  font-size: 14px;
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
}
@keyframes scanner-pulse {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5); }
  50% { opacity: 0.85; box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55); }
}
.scan-result-overlay {
  position: absolute;
  bottom: 24px;
  left: 16px;
  right: 16px;
  margin: 0;
  padding: 24px;
  border-radius: 16px;
  text-align: center;
  animation: result-in 0.4s ease-out;
}
.scan-result-overlay.success {
  background: rgba(var(--ion-color-success-rgb), 0.2);
  border: 2px solid var(--ion-color-success);
}
.scan-result-overlay.fail {
  background: rgba(var(--ion-color-danger-rgb), 0.2);
  border: 2px solid var(--ion-color-danger);
}
.scan-result-overlay .scan-result-icon {
  font-size: 56px;
  margin-bottom: 8px;
}
.scan-result-overlay.success .scan-result-icon { color: var(--ion-color-success); }
.scan-result-overlay.fail .scan-result-icon { color: var(--ion-color-danger); }
.scan-result-overlay .scan-result-text {
  font-weight: 600;
  margin: 0;
  color: var(--liga-chrome-text);
}
.scan-result-overlay .scan-result-user {
  margin: 4px 0 12px 0;
  color: var(--liga-chrome-text);
}
.scan-result-rescan-btn {
  --border-color: var(--ion-color-orange);
  --color: var(--ion-color-orange);
  --border-radius: 12px;
  --padding-top: 10px;
  --padding-bottom: 10px;
  --padding-start: 20px;
  --padding-end: 20px;
  font-weight: 600;
  margin-top: 4px;
}
.scan-result-rescan-btn:hover,
.scan-result-rescan-btn.ion-activated {
  --background: var(--ion-color-orange);
  --color: var(--liga-chrome-text);
  --border-color: var(--ion-color-orange);
}
.scanner-error {
  position: absolute;
  bottom: 24px;
  left: 16px;
  right: 16px;
  padding: 16px;
  background: rgba(235, 68, 90, 0.9);
  color: var(--liga-chrome-text);
  border-radius: 12px;
  text-align: center;
  margin: 0;
}
@keyframes result-in {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
@media (max-width: 768px) {
  .registrations-header-actions .header-btn-label {
    display: none;
  }
}
</style>
